import * as d3 from "d3";

export class AnswerDistributionDonut {
	draw(id, data, isMobile = false, showPercentage = false) {
		const container = document.getElementById(id);
		if (!container) return;

		// Clear existing chart
		container.innerHTML = "";

		/* ---------- FRONTEND COLOR MAP ---------- */
		const colorMap = {
			"Correct Answers": "#0A8F08", // Green
			"Incorrect Answers": "#B00020", // Red
			"Not Answered": "#FB923C", // Orange
			"Not Answer": "#FB923C", // Fallback
		};

		// Get container dimensions
		const containerWidth = container.clientWidth || 300;
		const containerHeight = container.clientHeight || 300;

		const width = containerWidth;
		const height = containerHeight;

		// Use scaling: Mobile gets larger doughnut relative to container
		const radiusMultiplier = isMobile ? 0.85 : 0.75;
		const radius = (Math.min(width, height) / 2) * radiusMultiplier;
		const innerRadius = radius * 0.6;

		/* ---------- SVG ---------- */
		const svg = d3
			.select(container)
			.append("svg")
			.attr("width", width)
			.attr("height", height)
			.append("g")
			.attr("transform", `translate(${width / 2}, ${height / 2})`);

		/* ---------- PIE ---------- */
		const pie = d3
			.pie()
			.sort(null)
			.value((d) => d.value)
			.padAngle(0.02);

		const pieData = pie(data);
		const legendData = pieData.filter((d) => d.data.value > 0);
		const arc = d3.arc().innerRadius(innerRadius).outerRadius(radius);
		// Label position for mobile (center of slice)
		const labelArc = d3.arc().innerRadius(innerRadius).outerRadius(radius);
		/* ---------- LEGEND (WRAPPING & RESPONSIVE) ---------- */
		if (isMobile) {
			const legendItemCount = legendData.length;
			const legendSvg = d3
				.select(container)
				.append("svg")
				.attr("width", width)
				.attr("height", isMobile ? (legendItemCount <= 2 ? 40 : 50) : 60);

			const legendGroup = legendSvg
				.append("g")
				.attr("transform", "translate(10, 20)");

			let xOffset = 0;
			let yOffset = 0;
			const itemSpacingX = isMobile ? 120 : 150;
			const itemSpacingY = 22;
			const maxWidth = width - 20;

			const legendItem = legendGroup
				.selectAll(".legend-item")
				.data(legendData)
				.enter()
				.append("g")
				.attr("class", "legend-item")
				.attr("transform", (d, i) => {
					if (xOffset + itemSpacingX > maxWidth) {
						xOffset = 0;
						yOffset += itemSpacingY;
					}

					const transform = `translate(${xOffset}, ${yOffset})`;
					xOffset += itemSpacingX;
					return transform;
				});

			legendItem
				.append("circle")
				.attr("r", 5)
				.attr("cx", 0)
				.attr("cy", 0)
				.attr("fill", (d) => colorMap[d.data.label]);

			legendItem
				.append("text")
				.attr("x", 12)
				.attr("y", 4)
				.attr("font-size", isMobile ? "12px" : "14px")
				.attr("fill", "#1E3A8A")
				.text((d) => {
					const label = d.data.label.replace(" Answers", "");
					const value = showPercentage ? `${d.data.percent}%` : d.data.value;

					return `${label} (${value})`;
				});
		}
		/* ---------- DONUT ---------- */
		svg
			.selectAll("path")
			.data(pieData)
			.enter()
			.append("path")
			.attr("d", arc)
			.attr("fill", (d) => colorMap[d.data.label]);

		/* ---------- CENTER TEXT ---------- */
		const centerGroup = svg.append("g").attr("text-anchor", "middle");

		centerGroup
			.append("text")
			.attr("dy", "-0.2em")
			.attr("font-size", isMobile ? "12px" : "18px")
			.attr("font-weight", 600)
			.attr("fill", "#333333")
			.text("Answer");

		centerGroup
			.append("text")
			.attr("dy", "1.3em")
			.attr("font-size", isMobile ? "12px" : "18px")
			.attr("font-weight", 600)
			.attr("fill", "#333333")
			.text("Distribution");

		/* ---------- MOBILE LABELS (Internal) ---------- */
		if (isMobile) {
			svg
				.selectAll("text.mobile-label")
				.data(pieData)
				.enter()
				.append("text")
				.attr("class", "mobile-label")
				.attr("transform", (d) => `translate(${labelArc.centroid(d)})`)
				.attr("dy", "0.35em")
				.attr("text-anchor", "middle")
				.attr("font-size", "14px")
				.attr("font-weight", 600)
				.attr("fill", "#FFFFFF")
				.text((d) => {
					if (d.data.value === 0) return "";
					return showPercentage ? `${d.data.percent}%` : d.data.value;
				});

			return; // Stop here for mobile, skip connectors
		}

/* ---------- DESKTOP CONNECTORS & LABELS ---------- */
		function midAngle(d) {
			return d.startAngle + (d.endAngle - d.startAngle) / 2;
		}

		if (!isMobile) {
			// 1. Calculate initial positions and sort by Y
			const labelData = pieData.map((d) => {
				if (d.data.value === 0) return { ...d, hidden: true };

				let angle = midAngle(d) - Math.PI / 2;
				
				// Handle single-slice case (approx 2*PI radians)
				const isSingleSlice = d.endAngle - d.startAngle > Math.PI * 1.8;
				if (isSingleSlice) {
					angle = 0;
				}

				const cos = Math.cos(angle);
				const sin = Math.sin(angle);
				
				// Determine side: Default based on hemisphere
				let isRight = cos > 0;
				let isCrossOver = false;

				// Optimization: If a slice is very near the top (start of circle),
				// force it to the Left side to utilize "Up/Left" space and prevent crowding
				// on the right. Do NOT apply to the dominant single-slice.
				// Threshold 0.25 radians is approx 14 degrees.
				if (!isSingleSlice && midAngle(d) < 0.25) {
					if (isRight) {
						isRight = false;
						isCrossOver = true;
					}
				}

				// Initial positions
				const startX = cos * radius;
				const startY = sin * radius;

				// The elbow is at the outer radius (midX)
				const midX = isRight ? radius : -radius;
				
				// Initial Y based on angle
				const initialY = sin * (radius + 20);

				return {
					...d,
					angle,
					isRight,
					isCrossOver,
					startX,
					startY,
					midX,
					y: initialY,
					hidden: false,
				};
			});

			const rightLabels = labelData
				.filter((d) => !d.hidden && d.isRight)
				.sort((a, b) => a.y - b.y);
			const leftLabels = labelData
				.filter((d) => !d.hidden && !d.isRight)
				.sort((a, b) => a.y - b.y);

			// 2. Relax positions to prevent overlap
			const minSpacing = 45; // Minimum vertical space between labels (px)

			function relax(group) {
				// Simple sweep: ensure each label is at least minSpacing below the previous
				for (let i = 1; i < group.length; i++) {
					const prev = group[i - 1];
					const curr = group[i];
					if (curr.y - prev.y < minSpacing) {
						curr.y = prev.y + minSpacing;
					}
				}
			}

			relax(rightLabels);
			relax(leftLabels);

			// Combine back for D3 binding
			/* ---------- CONNECTORS ---------- */
			svg
				.selectAll(".connector")
				.data(labelData)
				.enter()
				.append("path")
				.attr("class", "connector")
				.attr("stroke", "#D1D5DB")
				.attr("stroke-width", 2)
				.attr("fill", "none")
				.style("display", (d) => (d.hidden ? "none" : "block"))
				.attr("d", (d) => {
					if (d.hidden) return "";
					
					const endX = d.isRight ? radius + 30 : -(radius + 30);

					// Curve Logic: If we moved a Top-Right slice to the Left, 
					// use a Stepped Orthogonal Line to clear the top of the donut.
					if (d.isCrossOver) {
						const clearanceY = -(radius + 20); // Height to clear the donut top
						// Path: Start -> Up to Clearance -> Left to Elbow X -> Vertical to Label Y -> Out to Label
						return `M${d.startX},${d.startY} L${d.startX},${clearanceY} L${d.midX},${clearanceY} L${d.midX},${d.y} L${endX},${d.y}`;
					}
					
					// Standard Straight Line Connector
					// Slice Edge -> Elbow -> Label
					return `M${d.startX},${d.startY} L${d.midX},${d.y} L${endX},${d.y}`;
				});

			/* ---------- LABELS ---------- */
			const labels = svg
				.selectAll("g.label")
				.data(labelData)
				.enter()
				.append("g")
				.attr("transform", (d) => {
					if (d.hidden) return "translate(0,0)";
					
					const x = d.isRight ? radius + 45 : -(radius + 40);
					return `translate(${x}, ${d.y})`;
				})
				.style("text-anchor", (d) => (d.isRight ? "start" : "end"))
				.style("display", (d) => (d.hidden ? "none" : "block"));

			labels
				.append("text")
				.attr("font-size", "16px")
				.attr("font-weight", 600)
				.attr("fill", "#1E3A8A")
				.text((d) => d.data.label);

			labels
				.append("text")
				.attr("dy", "1.4em")
				.attr("font-size", "14px")
				.attr("fill", "#6B7280")
				.html(
					(d) =>
						`${d.data.value} | 
         <tspan font-weight="600" fill="${colorMap[d.data.label]}">
           ${d.data.percent}%
         </tspan>`
				);
		}
	}
}
